import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Gift, ArrowLeft, User, Mail, Lock, LogOut, 
  Trash2, ArrowRight, Upload, X, Loader2
} from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import PasswordInput from '../components/ui/PasswordInput';
import ConfirmDialog from '../components/ConfirmDialog';
import SubscriptionBadge from '../components/SubscriptionBadge';
import Avatar from '../components/ui/Avatar';
import { useAuthStore } from '../store/auth-store';
import { useSubscription } from '../lib/hooks/useSubscription';
import { supabase } from '../lib/supabase';
import { sendAvatarUpdateWebhook } from '../lib/webhooks';
import toast from 'react-hot-toast';
import { formatBytes } from '../lib/utils';

const SettingsPage = () => {
  const navigate = useNavigate();
  const { user, setUser, logout } = useAuthStore();
  const { subscription, expiryDate, limits, getStorageUsed } = useSubscription();
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [storageUsed, setStorageUsed] = useState(0);
  const [notesCount, setNotesCount] = useState(0);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }

    const fetchStats = async () => {
      const storage = await getStorageUsed();
      setStorageUsed(storage);

      const { count } = await supabase
        .from('notes')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);

      setNotesCount(count || 0);
    };

    fetchStats();
  }, [user, navigate, getStorageUsed]);

  const uploadAvatar = async (file: File) => {
    try {
      setUploading(true);

      if (!user) throw new Error('No user');

      // Check file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        throw new Error('File size must be less than 2MB');
      }

      // Check file type
      if (!file.type.startsWith('image/')) {
        throw new Error('File must be an image');
      }

      const fileExt = file.name.split('.').pop();
      const fileName = `${uuidv4()}.${fileExt}`;
      const filePath = `avatars/${fileName}`;

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      // Update user metadata
      const { error: updateError } = await supabase.auth.updateUser({
        data: { avatar_url: publicUrl }
      });

      if (updateError) throw updateError;

      // Update local user state
      setUser({
        ...user,
        avatar_url: publicUrl
      });

      // Send webhook
      await sendAvatarUpdateWebhook({
        email: user.email,
        name: user.name,
        avatar_url: publicUrl
      });

      toast.success('Avatar updated successfully');
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveAvatar = async () => {
    try {
      if (!user?.avatar_url) return;

      const { error } = await supabase.auth.updateUser({
        data: { avatar_url: null }
      });

      if (error) throw error;

      setUser({
        ...user,
        avatar_url: undefined
      });

      toast.success('Avatar removed');
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleUpdateProfile = async () => {
    if (!name.trim() || !email.trim()) {
      toast.error('Name and email are required');
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase.auth.updateUser({
        email,
        data: { name },
      });

      if (error) throw error;

      if (data.user) {
        setUser({
          id: data.user.id,
          email: data.user.email!,
          name: data.user.user_metadata.name,
          avatar_url: data.user.user_metadata.avatar_url
        });
        toast.success('Profile updated successfully');
      }
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (!currentPassword || !newPassword) {
      toast.error('Both current and new passwords are required');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('New password must be at least 8 characters long');
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      toast.success('Password updated successfully');
      setCurrentPassword('');
      setNewPassword('');
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setIsLoading(true);
    try {
      // Delete user settings first (this will cascade to other tables)
      const { error: settingsError } = await supabase
        .from('user_settings')
        .delete()
        .eq('id', user?.id);

      if (settingsError) throw settingsError;

      // Delete auth user
      const { error: authError } = await supabase.auth.admin.deleteUser(user?.id!);
      
      if (authError) throw authError;

      await supabase.auth.signOut();
      logout();
      toast.success('Account deleted successfully');
      navigate('/');
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      logout();
      toast.success('Logged out successfully');
      navigate('/');
    } catch (error: any) {
      toast.error(error.message);
    }
    setShowLogoutConfirm(false);
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length <= 50) {
      setName(value);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-purple-500/5">
      <header className="sticky top-0 border-b bg-background/80 backdrop-blur-sm z-40">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <h1 className="text-xl font-semibold">Settings</h1>
          <div className="w-10" />
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto space-y-8">
          {/* Avatar Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <h2 className="text-2xl font-semibold">Profile Picture</h2>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <Avatar
                  name={user?.name || ''}
                  url={user?.avatar_url}
                  size="lg"
                />
                <div>
                  <h3 className="font-medium">Avatar</h3>
                  <p className="text-sm text-muted-foreground">
                    Upload a picture to make your profile more personal
                  </p>
                </div>
              </div>

              <motion.div
                className={`relative border-2 border-dashed rounded-lg p-8 text-center ${
                  dragOver ? 'border-primary bg-primary/5' : 'border-muted-foreground/25'
                }`}
                animate={{ borderColor: dragOver ? 'hsl(var(--primary))' : '' }}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  if (!e.dataTransfer.files || e.dataTransfer.files.length === 0) return;
                  uploadAvatar(e.dataTransfer.files[0]);
                }}
              >
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    if (!e.target.files || e.target.files.length === 0) return;
                    uploadAvatar(e.target.files[0]);
                  }}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  disabled={uploading}
                />
                
                <div className="space-y-2">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                    <Upload className="w-5 h-5 text-primary" />
                  </div>
                  <div className="text-sm">
                    <span className="font-medium text-primary">Click to upload</span>
                    {' '}or drag and drop
                  </div>
                  <p className="text-xs text-muted-foreground">
                    PNG, JPG or GIF (max. 2MB)
                  </p>
                </div>

                {uploading && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-sm"
                  >
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Uploading...</span>
                    </div>
                  </motion.div>
                )}
              </motion.div>

              {user?.avatar_url && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleRemoveAvatar}
                >
                  <X className="w-4 h-4 mr-2" />
                  Remove Avatar
                </Button>
              )}
            </div>
          </motion.section>

          {/* Profile Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="space-y-6"
          >
            <h2 className="text-2xl font-semibold">Profile</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Display Name
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={name}
                    onChange={handleNameChange}
                    className="pl-9"
                    placeholder="Your name"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-9"
                    placeholder="your@email.com"
                  />
                </div>
              </div>
              <Button
                onClick={handleUpdateProfile}
                disabled={isLoading}
                className="w-full"
              >
                Update Profile
              </Button>
            </div>
          </motion.section>

          {/* Password Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-6"
          >
            <h2 className="text-2xl font-semibold">Security</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Current Password
                </label>
                <PasswordInput
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  New Password
                </label>
                <PasswordInput
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  minLength={8}
                />
              </div>
              <Button
                onClick={handleUpdatePassword}
                disabled={isLoading}
                className="w-full"
              >
                Update Password
              </Button>
            </div>
          </motion.section>

          {/* Subscription Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="space-y-6"
          >
            <h2 className="text-2xl font-semibold">Subscription</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-medium">
                      CodeNest {subscription.charAt(0).toUpperCase() + subscription.slice(1)}
                    </h3>
                    <SubscriptionBadge type={subscription} />
                  </div>
                  {expiryDate && (
                    <p className="text-sm text-muted-foreground">
                      Expires on {new Date(expiryDate).toLocaleDateString()}
                    </p>
                  )}
                </div>
                {subscription !== 'premium' && (
                  <Link to="/pricing">
                    <Button>
                      Upgrade Plan
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                )}
              </div>

              <div className="grid gap-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Notes Used</span>
                    <span className="font-medium">{notesCount}/{limits.maxNotes}</span>
                  </div>
                  <div className="h-2 bg-muted rounded-full">
                    <div
                      className="h-2 bg-primary rounded-full transition-all"
                      style={{ width: `${(notesCount / limits.maxNotes) * 100}%` }}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Storage Used</span>
                    <span className="font-medium">
                      {formatBytes(storageUsed)}/{formatBytes(limits.maxStorage)}
                    </span>
                  </div>
                  <div className="h-2 bg-muted rounded-full">
                    <div
                      className="h-2 bg-primary rounded-full transition-all"
                      style={{ width: `${(storageUsed / limits.maxStorage) * 100}%` }}
                    />
                  </div>
                </div>

                <Button
                  variant="outline"
                  onClick={() => navigate('/gifts')}
                  className="w-full"
                >
                  <Gift className="w-4 h-4 mr-2" />
                  View My Gift Codes
                </Button>
              </div>
            </div>
          </motion.section>

          {/* Account Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="space-y-6"
          >
            <h2 className="text-2xl font-semibold">Account</h2>
            <div className="space-y-4">
              <Button
                variant="destructive"
                onClick={() => setShowLogoutConfirm(true)}
                className="w-full"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
              <Button
                variant="destructive"
                onClick={() => setShowDeleteConfirm(true)}
                className="w-full"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Account
              </Button>
            </div>
          </motion.section>
        </div>
      </main>

      {/* Confirmation Dialogs */}
      <ConfirmDialog
        isOpen={showLogoutConfirm}
        title="Confirm Logout"
        description="Are you sure you want to log out of your account?"
        onConfirm={handleLogout}
        onCancel={() => setShowLogoutConfirm(false)}
      />

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Delete Account"
        description="Are you sure you want to delete your account? This action cannot be undone and all your data will be permanently deleted."
        onConfirm={handleDeleteAccount}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </div>
  );
};

export default SettingsPage;